import React, { useMemo } from "react";
import Button from "../../../shared/ui/Button";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import AccountStatusBadge from "./AccountStatusBadge";
import { BILLING_PLANS, setBillingPlan } from "../billingPlanStorage";
import {
  calculateFreeTierQuote,
  calculateSubscriberQuote,
  formatEuro,
  FREE_TIER_BANDS,
} from "../pricingCalculator";
import {
  calculateProjectedRating,
  formatDeltaDisplay,
  formatRatingDisplay,
} from "../utils/ratingSimulator";
import { useTranslation } from "../../../i18n";
import ProfessionalBenefitsBanner from "./ProfessionalBenefitsBanner";

function RatingImpactCard({ totalReviews, currentRating, selectedReviews }) {
  const { t } = useTranslation();
  const selectedCount = selectedReviews.length;

  const { projected, delta } = useMemo(
    () => calculateProjectedRating(totalReviews, currentRating, selectedReviews),
    [totalReviews, currentRating, selectedReviews]
  );

  const hasSelection = selectedCount > 0;
  const improves = delta > 0.004; // below display precision (+0.00) counts as flat

  return (
    <section className="mt-4 rounded-[12px] bg-[#f8fafc] p-4 ring-1 ring-[#e8ecf0]">
      <header className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748b]">
          {t("pricingPanel.ratingImpact")}
        </span>
        {hasSelection && (
          <span
            className={[
              "rounded-full px-2.5 py-0.5 text-[11px] font-bold tabular-nums transition-colors duration-300",
              improves
                ? "bg-[#ecfdf5] text-[#047857] ring-1 ring-[#a7f3d0]"
                : "bg-[#f1f5f9] text-[#64748b] ring-1 ring-[#e2e8f0]",
            ].join(" ")}
          >
            {formatDeltaDisplay(delta)} ★
          </span>
        )}
      </header>

      <div className="mt-3 flex items-center gap-2">
        <div className="flex-1 rounded-[10px] bg-white px-3 py-2.5 text-center ring-1 ring-[#e8ecf0]">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#94a3b8]">
            {t("pricingPanel.currentRating")}
          </p>
          <p className="mt-0.5 text-[20px] font-bold leading-tight tabular-nums text-[#0f172a]">
            {formatRatingDisplay(currentRating)}
            <span className="ml-1 text-[14px] text-[#f59e0b]">★</span>
          </p>
        </div>

        <MaterialIcon
          name="arrow_forward"
          className={[
            "shrink-0 text-[18px] transition-colors duration-300",
            hasSelection ? "text-[#006666]" : "text-[#cbd5e1]",
          ].join(" ")}
          aria-hidden
        />

        <div
          className={[
            "flex-1 rounded-[10px] px-3 py-2.5 text-center transition-all duration-300",
            hasSelection
              ? "bg-[#e6f4f4] ring-1 ring-[#006666]/30"
              : "bg-white ring-1 ring-[#e8ecf0]",
          ].join(" ")}
        >
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#94a3b8]">
            {t("pricingPanel.afterDeletion")}
          </p>
          <p
            className={[
              "mt-0.5 text-[20px] font-bold leading-tight tabular-nums transition-colors duration-300",
              hasSelection ? "text-[#006666]" : "text-[#cbd5e1]",
            ].join(" ")}
          >
            {hasSelection ? formatRatingDisplay(projected) : "—"}
            {hasSelection && <span className="ml-1 text-[14px] text-[#f59e0b]">★</span>}
          </p>
        </div>
      </div>

      <p className="mt-2.5 text-[11px] leading-snug text-[#94a3b8]">
        {hasSelection
          ? t("pricingPanel.ratingImpactBasedOn", { count: totalReviews })
          : t("pricingPanel.ratingImpactHint")}
      </p>
    </section>
  );
}

function TierProgressBar({ progress, reviewCount }) {
  const { t } = useTranslation();
  const { percent, milestones, currentTier } = progress;

  return (
    <section className="mt-4">
      <header className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-[#64748b]">
        <span>{t("pricingPanel.tierProgress")}</span>
        <span className="text-[#006666]">{currentTier.label}</span>
      </header>
      <div className="relative h-3 overflow-hidden rounded-full bg-[#e2e8f0]">
        <span
          className="absolute inset-y-0 left-0 block rounded-full bg-gradient-to-r from-[#006666] to-[#00a3a3] transition-all duration-500 ease-out"
          style={{ width: `${Math.max(reviewCount > 0 ? 8 : 0, percent)}%` }}
        />
        {milestones.map((m) => (
          <span
            key={m.label}
            className="absolute top-1/2 h-2 w-2 -translate-y-1/2 rounded-full border-2 border-white bg-[#94a3b8]"
            style={{ left: `calc(${m.percent}% - 4px)` }}
            title={m.label}
          />
        ))}
      </div>
      <footer className="mt-2 flex justify-between text-[10px] font-medium text-[#94a3b8]">
        {FREE_TIER_BANDS.map((band) => (
          <span key={band.id}>{formatEuro(band.pricePerReview)}</span>
        ))}
      </footer>
    </section>
  );
}

const ReviewSelectionPricingPanel = ({
  reviewCount,
  billingPlan,
  accountStatus,
  lockPlanSwitch = false,
  subscriberFreeRemaining,
  freeAllowance = 2,
  freeUsed = 0,
  periodEnd = null,
  isProfessional = false,
  totalReviews = 0,
  currentRating = 0,
  selectedReviews = [],
  onBillingPlanChange,
  onClear,
  onContinue,
}) => {
  const { t } = useTranslation();
  const isSubscriber = billingPlan === BILLING_PLANS.SUBSCRIBER;

  const freeQuote = useMemo(
    () => calculateFreeTierQuote(reviewCount),
    [reviewCount]
  );
  const subQuote = useMemo(
    () => calculateSubscriberQuote(reviewCount, subscriberFreeRemaining),
    [reviewCount, subscriberFreeRemaining]
  );

  const manualInvoicingNote = t("pricingPanel.manualInvoicingNote");

  return (
    <aside className="w-full" aria-label={t("pricingPanel.livePricingAria")}>
      <article
        className={[
          "flex max-h-[min(720px,calc(100dvh-4rem))] flex-col overflow-hidden rounded-[18px] border border-[#e8ecf0] bg-white shadow-[0_16px_48px_rgba(15,23,42,0.08)]",
          "lg:max-h-[calc(100dvh-4rem)]",
        ].join(" ")}
      >
        <header className="shrink-0 border-b border-[#e8ecf0] bg-gradient-to-r from-[#0f172a] to-[#1e3a5f] px-5 py-4 text-white">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/70">
            {t("pricingPanel.livePricing")}
          </p>
          {lockPlanSwitch && accountStatus ? (
            <div className="mt-3">
              <AccountStatusBadge status={accountStatus} className="!bg-white/15 !text-white !ring-white/30" />
              <p className="mt-2 text-[12px] text-white/75">
                {t("pricingPanel.pricingByStatus")}
              </p>
            </div>
          ) : (
            <nav className="mt-3 flex gap-1 rounded-full bg-white/10 p-1" aria-label={t("pricingPanel.billingPlanAria")}>
              <button
                type="button"
                onClick={() => {
                  setBillingPlan(BILLING_PLANS.FREE);
                  onBillingPlanChange?.(BILLING_PLANS.FREE);
                }}
                className={[
                  "flex-1 rounded-full py-2 text-[12px] font-semibold transition",
                  !isSubscriber ? "bg-white text-[#0f172a]" : "text-white/80 hover:text-white",
                ].join(" ")}
              >
                {t("pricingPanel.freeTier")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setBillingPlan(BILLING_PLANS.SUBSCRIBER);
                  onBillingPlanChange?.(BILLING_PLANS.SUBSCRIBER);
                }}
                className={[
                  "flex-1 rounded-full py-2 text-[12px] font-semibold transition",
                  isSubscriber ? "bg-white text-[#006666]" : "text-white/80 hover:text-white",
                ].join(" ")}
              >
                {t("pricingPanel.subscriber")}
              </button>
            </nav>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="p-5">
          <div className="mb-4 flex items-center gap-2 rounded-[12px] bg-[#ecfdf5] px-4 py-2.5 ring-1 ring-[#a7f3d0]">
            <span className="h-2 w-2 shrink-0 rounded-full bg-[#10b981]" aria-hidden />
            <span className="text-[13px] font-semibold text-[#047857]">
              {t("pricingPanel.successRate")}
            </span>
          </div>

          {isProfessional ? (
            <ProfessionalBenefitsBanner
              freeRemaining={subQuote.freeRemaining}
              freeAllowance={freeAllowance}
              freeUsed={freeUsed}
              periodEnd={periodEnd}
              compact
              className="mb-4"
            />
          ) : null}

          <div className="flex items-center justify-between rounded-[12px] bg-[#f8fafc] px-4 py-3 ring-1 ring-[#e8ecf0]">
            <span className="text-[13px] font-medium text-[#64748b]">
              {t("pricingPanel.selectedReviews")}
            </span>
            <span className="text-[22px] font-bold tabular-nums text-[#0f172a]">
              {reviewCount}
            </span>
          </div>

          {totalReviews > 0 && currentRating > 0 && (
            <RatingImpactCard
              totalReviews={totalReviews}
              currentRating={currentRating}
              selectedReviews={selectedReviews}
            />
          )}

          {!isSubscriber && (
            <>
              <div className="mt-5 text-center">
                <p className="text-[12px] font-semibold uppercase tracking-wide text-[#64748b]">
                  {t("pricingPanel.pricePerReview")}
                </p>
                <p className="mt-1 text-[44px] font-bold leading-none tracking-tight text-[#006666] transition-all duration-300">
                  {formatEuro(freeQuote.pricePerReview)}
                </p>
                <p className="mt-2 text-[13px] text-[#64748b]">
                  {t("pricingPanel.activePackage")}{" "}
                  <span className="font-semibold text-[#0f172a]">
                    {freeQuote.tier.label}
                  </span>
                </p>
              </div>

              <TierProgressBar
                progress={freeQuote.progress}
                reviewCount={reviewCount}
              />

              {freeQuote.nudge.highlight && reviewCount < 11 && (
                <p className="mt-4 rounded-[12px] border border-[#006666]/25 bg-[#e6f4f4] px-4 py-3 text-[13px] font-medium leading-snug text-[#004d4d]">
                  {freeQuote.nudge.message}
                </p>
              )}

              {freeQuote.savingsVsTopTier > 0 && reviewCount > 0 && (
                <p className="mt-3 text-center text-[12px] text-emerald-700">
                  {t("pricingPanel.savingsVs", {
                    amount: formatEuro(freeQuote.savingsVsTopTier),
                  })}
                </p>
              )}

              <div className="mt-5 flex items-end justify-between border-t border-[#f1f5f9] pt-4">
                <span className="text-[14px] font-medium text-[#64748b]">
                  {t("pricingPanel.estimatedTotalThisCase")}
                </span>
                <span className="text-[22px] font-bold text-[#0f172a] transition-all duration-300">
                  {formatEuro(freeQuote.total)}
                </span>
              </div>

              <p className="mt-3 text-[11px] leading-relaxed text-[#94a3b8]">
                {manualInvoicingNote}
              </p>
            </>
          )}

          {isSubscriber && (
            <>
              {subQuote.freeApplied > 0 ? (
                <div className="mt-4 rounded-[12px] border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <p className="text-[13px] font-semibold text-emerald-900">
                    {t("pricingPanel.freeAppliedHighlight", {
                      count: subQuote.freeApplied,
                    })}
                  </p>
                  <p className="mt-1 text-[12px] text-emerald-800/90">
                    {t("pricingPanel.freeAppliedDetail")}
                  </p>
                </div>
              ) : subQuote.freeRemaining === 0 ? (
                <p className="mt-4 rounded-[12px] border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-900">
                  {t("pricingPanel.noFreeRemaining")}
                </p>
              ) : null}

              <ul className="mt-4 space-y-2 rounded-[12px] border border-[#e8ecf0] bg-[#f8fafc] p-4 text-[13px]">
                <li className="flex justify-between">
                  <span className="text-[#64748b]">{t("pricingPanel.freeThisMonth")}</span>
                  <span className="font-semibold text-[#006666]">
                    {subQuote.freeRemaining}
                  </span>
                </li>
                <li className="flex justify-between">
                  <span className="text-[#64748b]">{t("pricingPanel.freeAppliedLabel")}</span>
                  <span className="font-semibold text-[#0f172a]">
                    {subQuote.freeApplied}
                  </span>
                </li>
                <li className="flex justify-between">
                  <span className="text-[#64748b]">{t("pricingPanel.paidReviews")}</span>
                  <span className="font-semibold text-[#0f172a]">
                    {subQuote.paidCount} × {formatEuro(subQuote.pricePerPaidReview)}
                  </span>
                </li>
              </ul>

              <div className="mt-5 text-center">
                <p className="text-[12px] font-semibold uppercase tracking-wide text-[#64748b]">
                  {t("pricingPanel.perPaidReview")}
                </p>
                <p className="mt-1 text-[44px] font-bold leading-none text-[#006666]">
                  {formatEuro(subQuote.pricePerPaidReview)}
                </p>
              </div>

              <div className="mt-5 flex items-end justify-between border-t border-[#f1f5f9] pt-4">
                <span className="text-[14px] font-medium text-[#64748b]">
                  {t("pricingPanel.estimatedTotalThisCase")}
                </span>
                <span className="text-[22px] font-bold text-[#0f172a]">
                  {formatEuro(subQuote.caseTotal)}
                </span>
              </div>

              <p className="mt-2 text-[12px] text-[#64748b]">
                {t("pricingPanel.planBilled", {
                  amount: formatEuro(subQuote.monthlySubscription),
                })}
              </p>

              <p className="mt-3 text-[11px] leading-relaxed text-[#94a3b8]">
                {manualInvoicingNote}
              </p>

              {reviewCount > 0 && (
                <p className="mt-3 rounded-[10px] bg-[#f1f5f9] px-3 py-2 text-[12px] text-[#475569]">
                  {t("pricingPanel.submissionSummary", {
                    freeApplied: subQuote.freeApplied,
                    paidCount: subQuote.paidCount,
                    total: formatEuro(subQuote.caseTotal),
                  })}
                </p>
              )}
            </>
          )}

          <div className="mt-6 flex flex-col gap-2">
            <Button
              type="button"
              variant="teal"
              className="!h-11 !w-full !rounded-full !text-[14px]"
              disabled={reviewCount === 0}
              onClick={onContinue}
            >
              {t("pricingPanel.continueToCase")}
            </Button>
            <button
              type="button"
              onClick={onClear}
              className="rounded-full py-2 text-[13px] font-medium text-[#64748b] hover:text-[#0f172a]"
            >
              {t("pricingPanel.clearSelection")}
            </button>
          </div>
        </div>
        </div>
      </article>
    </aside>
  );
};

export default ReviewSelectionPricingPanel;
